import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker&url';

export interface PdfViewerOverlay {
  id: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string;
}

export interface PdfViewerTextHighlight {
  id: string;
  page?: number | null;
  text: string;
  contextText?: string | null;
  label?: string;
}

export interface PdfViewerSelectionMetadata {
  sourceSurface?: string;
  label?: string;
  documentId?: string | null;
  versionId?: string | null;
  documentTitle?: string | null;
  matterId?: string | null;
  clientId?: string | null;
}

interface PdfViewerProps {
  data: Uint8Array;
  viewMode?: 'single' | 'continuous';
  className?: string;
  surfaceClassName?: string;
  onPageChange?: (page: number, total: number) => void;
  targetPage?: number | null;
  overlays?: PdfViewerOverlay[];
  textHighlights?: PdfViewerTextHighlight[];
  selectionMetadata?: PdfViewerSelectionMetadata;
  activeOverlayId?: string | null;
  overlayPointerMode?: 'auto' | 'none';
  onOverlaySelect?: (overlayId: string) => void;
  onCanvasClick?: (point: { page: number; x: number; y: number }) => void;
  onOverlayMove?: (overlayId: string, point: { page: number; x: number; y: number }) => void;
  onOverlayMoveEnd?: (overlayId: string, point: { page: number; x: number; y: number }) => void;
}

interface PdfSelectableTextItem {
  id: string;
  page: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
}

interface PdfSearchResult {
  id: string;
  page: number;
  text: string;
  contextText: string | null;
}

function normalizeSearchText(value: string): string {
  return value.toLowerCase().replace(/[^\w\s().§-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function pageMatchesContext(pageText: string, contextText?: string | null): boolean {
  if (!contextText?.trim()) return true;
  const normalizedPage = normalizeSearchText(pageText);
  const normalizedContext = normalizeSearchText(contextText);
  if (!normalizedContext) return true;
  if (normalizedPage.includes(normalizedContext)) return true;
  const tokens = normalizedContext
    .split(' ')
    .filter((token) => token.length >= 4)
    .slice(0, 24);
  if (tokens.length === 0) return true;
  const matches = tokens.filter((token) => normalizedPage.includes(token)).length;
  return matches >= Math.min(12, Math.max(6, Math.ceil(tokens.length * 0.75)));
}

function pageMatchesHighlight(pageText: string, highlight: PdfViewerTextHighlight): boolean {
  const normalizedPage = normalizeSearchText(pageText);
  const normalizedNeedle = normalizeSearchText(highlight.text);
  if (!normalizedNeedle || !normalizedPage.includes(normalizedNeedle)) return false;
  return pageMatchesContext(pageText, highlight.contextText);
}

async function textContentForPage(pdf: any, page: number): Promise<string> {
  const pdfPage = await pdf.getPage(page);
  const textContent = await pdfPage.getTextContent();
  return (textContent.items || [])
    .map((item: any) => (typeof item?.str === 'string' ? item.str : ''))
    .filter(Boolean)
    .join(' ');
}

function nextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

async function buildSelectableTextItemsForPage({
  pdf,
  pdfjsLib,
  page,
}: {
  pdf: any;
  pdfjsLib: any;
  page: number;
}): Promise<PdfSelectableTextItem[]> {
  const pdfPage = await pdf.getPage(page);
  const viewport = pdfPage.getViewport({ scale: 1 });
  const textContent = await pdfPage.getTextContent();
  const rawDims = viewport.rawDims || {
    pageWidth: viewport.width,
    pageHeight: viewport.height,
    pageX: 0,
    pageY: 0,
  };
  const baseTransform = [1, 0, 0, -1, -rawDims.pageX, rawDims.pageY + rawDims.pageHeight];

  return (textContent.items || [])
    .map((item: any, index: number): PdfSelectableTextItem | null => {
      const text = typeof item?.str === 'string' ? item.str : '';
      if (!text.trim()) return null;
      const transform = pdfjsLib.Util.transform(baseTransform, item.transform);
      const fontHeight = Math.max(1, Math.hypot(transform[2], transform[3]));
      // PDF.js positions text by baseline minus font ascent. A full-height subtraction
      // puts the selectable span too high on many legal PDFs.
      const fontAscent = fontHeight * 0.82;
      const itemWidth = Math.max(1, Number(item.width || 0));
      return {
        id: `${page}-${index}`,
        page,
        text,
        x: transform[4],
        y: transform[5] - fontAscent,
        width: itemWidth,
        height: fontHeight,
        fontSize: fontHeight,
      };
    })
    .filter((item: PdfSelectableTextItem | null): item is PdfSelectableTextItem => Boolean(item));
}

async function buildTextOverlaysForPage({
  pdf,
  pdfjsLib,
  page,
  viewport,
  scale,
  highlights,
}: {
  pdf: any;
  pdfjsLib: any;
  page: number;
  viewport: any;
  scale: number;
  highlights: PdfViewerTextHighlight[];
}): Promise<PdfViewerOverlay[]> {
  const pdfPage = await pdf.getPage(page);
  const textContent = await pdfPage.getTextContent();
  const overlaysForPage: PdfViewerOverlay[] = [];
  const textItems = (textContent.items || [])
    .map((item: any) => {
      const rawText = typeof item?.str === 'string' ? item.str : '';
      if (!rawText.trim()) return null;
      const transform = pdfjsLib.Util.transform(viewport.transform, item.transform);
      const itemWidth = Number(item.width || 0) * scale;
      const itemHeight = Math.max(Number(item.height || 0) * scale, Math.abs(transform[3]) || 10);
      return {
        rawText,
        normalized: rawText.toLowerCase(),
        x: transform[4] / scale,
        y: (transform[5] - itemHeight) / scale,
        width: itemWidth / scale,
        height: itemHeight / scale,
      };
    })
    .filter(Boolean) as Array<{
      rawText: string;
      normalized: string;
      x: number;
      y: number;
      width: number;
      height: number;
    }>;

  for (const highlight of highlights) {
    const needle = highlight.text.trim().toLowerCase();
    if (!needle) continue;
    if (!highlight.page && !pageMatchesContext(textItems.map((item) => item.rawText).join(' '), highlight.contextText)) {
      continue;
    }

    for (const item of textItems) {
      const matchIndex = item.normalized.indexOf(needle);
      if (matchIndex < 0) continue;
      const safeLength = Math.max(1, item.rawText.length);
      const matchLeft = item.width * (matchIndex / safeLength);
      const matchWidth = Math.max(10, item.width * (highlight.text.length / safeLength));
      overlaysForPage.push({
        id: highlight.id,
        page,
        x: item.x + matchLeft,
        y: item.y,
        width: matchWidth,
        height: item.height,
        label: highlight.label,
      });
      break;
    }

    if (overlaysForPage.some((overlay) => overlay.id === highlight.id)) continue;

    const joinedParts: Array<{ itemIndex: number; start: number; end: number }> = [];
    let joinedText = '';
    textItems.forEach((item, itemIndex) => {
      if (joinedText) joinedText += ' ';
      const start = joinedText.length;
      joinedText += item.normalized;
      joinedParts.push({ itemIndex, start, end: joinedText.length });
    });

    const joinedIndex = joinedText.indexOf(needle);
    if (joinedIndex < 0) continue;
    const joinedEnd = joinedIndex + needle.length;
    const matchingParts = joinedParts.filter((part) => part.end >= joinedIndex && part.start <= joinedEnd);
    const matchingItems = matchingParts.map((part) => textItems[part.itemIndex]).filter(Boolean);
    if (!matchingItems.length) continue;

    const left = Math.min(...matchingItems.map((item) => item.x));
    const top = Math.min(...matchingItems.map((item) => item.y));
    const right = Math.max(...matchingItems.map((item) => item.x + item.width));
    const bottom = Math.max(...matchingItems.map((item) => item.y + item.height));
    overlaysForPage.push({
      id: highlight.id,
      page,
      x: left,
      y: top,
      width: Math.max(10, right - left),
      height: Math.max(10, bottom - top),
      label: highlight.label,
    });
  }
  return overlaysForPage;
}

function prioritizePages(pageCount: number, targetPage?: number | null): number[] {
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);
  if (!targetPage || pageCount <= 1) return pages;
  const safeTarget = Math.max(1, Math.min(pageCount, Math.trunc(targetPage)));
  const priority = [
    safeTarget,
    safeTarget - 1,
    safeTarget + 1,
    safeTarget - 2,
    safeTarget + 2,
  ].filter((page, index, list) => page >= 1 && page <= pageCount && list.indexOf(page) === index);
  const prioritySet = new Set(priority);
  return [...priority, ...pages.filter((page) => !prioritySet.has(page))];
}

export function PdfViewer({
  data,
  viewMode = 'single',
  className,
  surfaceClassName,
  onPageChange,
  targetPage,
  overlays,
  textHighlights,
  selectionMetadata,
  activeOverlayId,
  overlayPointerMode,
  onOverlaySelect,
  onCanvasClick,
  onOverlayMove,
  onOverlayMoveEnd,
}: PdfViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const continuousCanvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const continuousPageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const continuousCanvasRefCallbacks = useRef<Map<number, (element: HTMLCanvasElement | null) => void>>(new Map());
  const continuousPageRefCallbacks = useRef<Map<number, (element: HTMLDivElement | null) => void>>(new Map());
  const continuousObserverRef = useRef<IntersectionObserver | null>(null);
  const pdfRef = useRef<any>(null);
  const singleCanvasRenderRequestRef = useRef<number | null>(null);
  const continuousRenderRequestRef = useRef<number | null>(null);
  const continuousRenderGenerationRef = useRef(0);
  const renderingContinuousPagesRef = useRef<Set<number>>(new Set());
  const wantedContinuousPagesRef = useRef<Set<number>>(new Set());
  const continuousRenderSignaturesRef = useRef<Map<number, string>>(new Map());
  const renderedContinuousPagesRef = useRef<Set<number>>(new Set());
  const pageTextCacheRef = useRef<Map<number, string>>(new Map());
  const findRequestRef = useRef(0);
  const autoScrolledTargetRef = useRef<string | null>(null);
  const renderScaleRef = useRef(1);
  const continuousScaleRef = useRef<Map<number, number>>(new Map());
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [jumpPageInput, setJumpPageInput] = useState('1');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renderKey, setRenderKey] = useState(0);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [defaultContinuousPageSize, setDefaultContinuousPageSize] = useState<{ width: number; height: number } | null>(null);
  const [continuousPageSizes, setContinuousPageSizes] = useState<Record<number, { width: number; height: number }>>({});
  const [visibleContinuousPages, setVisibleContinuousPages] = useState<Set<number>>(() => new Set([1]));
  const [renderedContinuousPages, setRenderedContinuousPages] = useState<Set<number>>(() => new Set());
  const [textHighlightOverlays, setTextHighlightOverlays] = useState<PdfViewerOverlay[]>([]);
  const [selectableTextItems, setSelectableTextItems] = useState<Record<number, PdfSelectableTextItem[]>>({});
  const [findQuery, setFindQuery] = useState('');
  const [findResults, setFindResults] = useState<PdfSearchResult[]>([]);
  const [activeFindIndex, setActiveFindIndex] = useState<number | null>(null);
  const [findSearching, setFindSearching] = useState(false);
  const [lastFindQuery, setLastFindQuery] = useState('');
  const textHighlightsKey = useMemo(
    () =>
      (textHighlights || [])
        .map((highlight) => `${highlight.id}:${highlight.page ?? 'any'}:${highlight.text}:${highlight.contextText ?? ''}`)
        .join('|'),
    [textHighlights],
  );
  const overlayPagesKey = useMemo(
    () => (overlays || []).map((overlay) => overlay.page).sort((a, b) => a - b).join('|'),
    [overlays],
  );
  const activeTextSearch = useMemo(() => {
    if (!activeOverlayId || !textHighlights?.length) return null;
    const highlight = textHighlights.find((item) => item.id === activeOverlayId && item.text.trim());
    if (!highlight || highlight.page) return null;
    return highlight;
  }, [activeOverlayId, textHighlights]);
  const activeTextSearchKey = activeTextSearch
    ? `${activeTextSearch.id}|${activeTextSearch.text}|${activeTextSearch.contextText ?? ''}`
    : null;
  const [textSearchResult, setTextSearchResult] = useState<{ key: string; page: number | null } | null>(null);
  const textSearchPending = Boolean(activeTextSearchKey && textSearchResult?.key !== activeTextSearchKey);
  const effectiveTargetPage =
    activeTextSearchKey && textSearchResult?.key === activeTextSearchKey && textSearchResult.page
      ? textSearchResult.page
      : targetPage;
  const activeFindResult =
    activeFindIndex != null && activeFindIndex >= 0 && activeFindIndex < findResults.length
      ? findResults[activeFindIndex]
      : null;
  const effectiveTextHighlights = useMemo(
    () => [...(textHighlights || [])],
    [textHighlights],
  );
  const effectiveActiveOverlayId = activeFindResult ? activeFindResult.id : activeOverlayId;
  const dragRef = useRef<{
    overlayId: string;
    page: number;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    scale: number;
    currentX: number;
    currentY: number;
    moved: boolean;
  } | null>(null);

  const requestSinglePageRender = useCallback(() => {
    if (singleCanvasRenderRequestRef.current != null) {
      window.cancelAnimationFrame(singleCanvasRenderRequestRef.current);
    }
    singleCanvasRenderRequestRef.current = window.requestAnimationFrame(() => {
      singleCanvasRenderRequestRef.current = null;
      setRenderKey((prev) => prev + 1);
    });
  }, []);

  const setSingleCanvasRef = useCallback(
    (element: HTMLCanvasElement | null) => {
      canvasRef.current = element;
      if (element) {
        requestSinglePageRender();
      }
    },
    [requestSinglePageRender],
  );

  const requestContinuousRender = useCallback(() => {
    if (continuousRenderRequestRef.current != null) return;
    continuousRenderRequestRef.current = window.requestAnimationFrame(() => {
      continuousRenderRequestRef.current = null;
      setRenderKey((prev) => prev + 1);
    });
  }, []);

  const setContinuousPageRef = useCallback(
    (page: number, element: HTMLDivElement | null) => {
      const existing = continuousPageRefs.current.get(page);
      if (existing === element) return;
      if (existing && continuousObserverRef.current) {
        continuousObserverRef.current.unobserve(existing);
      }
      if (element) {
        continuousPageRefs.current.set(page, element);
        continuousObserverRef.current?.observe(element);
      } else {
        continuousPageRefs.current.delete(page);
      }
    },
    [],
  );

  const setContinuousCanvasRef = useCallback(
    (page: number, element: HTMLCanvasElement | null) => {
      const existing = continuousCanvasRefs.current.get(page);
      if (existing === element) return;
      if (element) {
        continuousCanvasRefs.current.set(page, element);
        if (wantedContinuousPagesRef.current.has(page)) {
          requestContinuousRender();
        }
      } else {
        continuousCanvasRefs.current.delete(page);
      }
    },
    [requestContinuousRender],
  );

  const getContinuousPageRef = useCallback(
    (page: number) => {
      const existing = continuousPageRefCallbacks.current.get(page);
      if (existing) return existing;
      const callback = (element: HTMLDivElement | null) => setContinuousPageRef(page, element);
      continuousPageRefCallbacks.current.set(page, callback);
      return callback;
    },
    [setContinuousPageRef],
  );

  const getContinuousCanvasRef = useCallback(
    (page: number) => {
      const existing = continuousCanvasRefCallbacks.current.get(page);
      if (existing) return existing;
      const callback = (element: HTMLCanvasElement | null) => setContinuousCanvasRef(page, element);
      continuousCanvasRefCallbacks.current.set(page, callback);
      return callback;
    },
    [setContinuousCanvasRef],
  );

  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      setCanvasSize({ width: 0, height: 0 });
      setTextHighlightOverlays([]);
      setSelectableTextItems({});
      try {
        const pdfjsLib = await import('pdfjs-dist');
        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
          pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        }

        // Pass a copy to PDF.js worker so upstream buffers stay stable for save/overlay logic.
        const loadingTask = pdfjsLib.getDocument({ data: data.slice(0) });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
        setDefaultContinuousPageSize(null);
        setContinuousPageSizes({});
        renderedContinuousPagesRef.current = new Set();
        setRenderedContinuousPages(new Set());
        setVisibleContinuousPages(new Set([1]));
        setSelectableTextItems({});
        setTextSearchResult(null);
        setFindQuery('');
        setFindResults([]);
        setActiveFindIndex(null);
        setLastFindQuery('');
        autoScrolledTargetRef.current = null;
        continuousScaleRef.current = new Map();
        continuousRenderGenerationRef.current += 1;
        renderingContinuousPagesRef.current = new Set();
        wantedContinuousPagesRef.current = new Set();
        continuousRenderSignaturesRef.current = new Map();
        pageTextCacheRef.current = new Map();
        findRequestRef.current += 1;
        setPageNumber((previous) => Math.max(1, Math.min(previous, pdf.numPages)));
        setLoading(false);
        setRenderKey((prev) => prev + 1);
      } catch {
        if (cancelled) return;
        setError('Unable to render PDF preview.');
        setLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (singleCanvasRenderRequestRef.current != null) {
        window.cancelAnimationFrame(singleCanvasRenderRequestRef.current);
        singleCanvasRenderRequestRef.current = null;
      }
      if (continuousRenderRequestRef.current != null) {
        window.cancelAnimationFrame(continuousRenderRequestRef.current);
        continuousRenderRequestRef.current = null;
      }
      if (pdfRef.current) {
        pdfRef.current.destroy?.();
        pdfRef.current = null;
      }
    };
  }, [data]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => setRenderKey((prev) => prev + 1));
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setJumpPageInput(String(pageNumber));
  }, [pageNumber]);

  useEffect(() => {
    if (viewMode !== 'continuous' || !surfaceRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleContinuousPages((previous) => {
          const next = new Set(previous);
          for (const entry of entries) {
            const page = Number((entry.target as HTMLElement).dataset.pdfPage || 0);
            if (!page) continue;
            if (entry.isIntersecting) next.add(page);
            else next.delete(page);
          }
          if (next.size === 0) next.add(pageNumber);
          const same = next.size === previous.size && [...next].every((page) => previous.has(page));
          return same ? previous : next;
        });
      },
      {
        root: surfaceRef.current,
        rootMargin: '900px 0px',
        threshold: 0.01,
      },
    );
    continuousObserverRef.current = observer;
    continuousPageRefs.current.forEach((element) => observer.observe(element));
    return () => {
      observer.disconnect();
      if (continuousObserverRef.current === observer) {
        continuousObserverRef.current = null;
      }
    };
  }, [pageNumber, pageCount, viewMode]);

  useEffect(() => {
    const renderPage = async () => {
      const pdf = pdfRef.current;
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!pdf || !canvas || !container) {
        if (viewMode === 'single') requestSinglePageRender();
        return;
      }

      try {
        const page = await pdf.getPage(pageNumber);
        const unscaled = page.getViewport({ scale: 1 });
        if (container.clientWidth <= 0) {
          requestSinglePageRender();
          return;
        }
        const maxWidth = Math.max(320, container.clientWidth - 40);
        const scale = Math.min(2, maxWidth / unscaled.width);
        const viewport = page.getViewport({ scale });

        const context = canvas.getContext('2d');
        if (!context) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        renderScaleRef.current = scale;
        setCanvasSize({ width: viewport.width, height: viewport.height });

        await page.render({ canvasContext: context, viewport }).promise;
        const pdfjsLib = await import('pdfjs-dist');
        setSelectableTextItems({
          [pageNumber]: await buildSelectableTextItemsForPage({
            pdf,
            pdfjsLib,
            page: pageNumber,
          }),
        });
        if (effectiveTextHighlights.length) {
          const pageHighlights = effectiveTextHighlights.filter((highlight) => !highlight.page || highlight.page === pageNumber);
          const generated = pageHighlights.length
            ? await buildTextOverlaysForPage({
                pdf,
                pdfjsLib,
                page: pageNumber,
                viewport,
                scale,
                highlights: pageHighlights,
              })
            : [];
          setTextHighlightOverlays(generated);
        } else {
          setTextHighlightOverlays([]);
        }
        setError(null);
      } catch {
        setError('Unable to render PDF page.');
      }
    };

    if (!loading && viewMode === 'single') {
      renderPage();
    }
  }, [pageNumber, renderKey, loading, viewMode, effectiveTextHighlights, requestSinglePageRender]);

  useEffect(() => {
    const renderContinuousPages = async () => {
      const pdf = pdfRef.current;
      const container = containerRef.current;
      if (!pdf || !container || viewMode !== 'continuous') return;

      try {
        const generation = continuousRenderGenerationRef.current;
        const pdfjsLib = await import('pdfjs-dist');
        const maxWidth = Math.max(320, container.clientWidth - 48);
        const wantedPages = new Set<number>();
        const addPage = (page: number | null | undefined) => {
          if (!page) return;
          const safePage = Math.max(1, Math.min(pdf.numPages, Math.trunc(page)));
          wantedPages.add(safePage);
        };
        const addPageWindow = (page: number | null | undefined, radius = 1) => {
          if (!page) return;
          for (let offset = -radius; offset <= radius; offset += 1) {
            addPage(Number(page) + offset);
          }
        };

        visibleContinuousPages.forEach((page) => addPageWindow(page, 1));
        addPageWindow(pageNumber, 1);
        addPageWindow(effectiveTargetPage, 2);
        addPageWindow(activeFindResult?.page, 1);
        for (const highlight of effectiveTextHighlights) {
          addPageWindow(highlight.page, 1);
        }
        for (const page of overlayPagesKey.split('|')) {
          addPageWindow(Number(page), 1);
        }
        if (wantedPages.size === 0) addPage(1);
        wantedContinuousPagesRef.current = wantedPages;

        const renderOrder = prioritizePages(pdf.numPages, effectiveTargetPage);
        for (const pageIndex of renderOrder) {
          if (!wantedPages.has(pageIndex)) continue;
          const canvas = continuousCanvasRefs.current.get(pageIndex);
          if (!canvas) {
            requestContinuousRender();
            continue;
          }
          if (renderingContinuousPagesRef.current.has(pageIndex)) continue;
          renderingContinuousPagesRef.current.add(pageIndex);
          try {
            const page = await pdf.getPage(pageIndex);
            if (generation !== continuousRenderGenerationRef.current || pdfRef.current !== pdf) return;
            const unscaled = page.getViewport({ scale: 1 });
            const scale = Math.min(2, maxWidth / unscaled.width);
            const viewport = page.getViewport({ scale });
            const context = canvas.getContext('2d');
            if (!context) continue;
            const renderSignature = `${Math.round(viewport.width)}x${Math.round(viewport.height)}|${textHighlightsKey}`;
            const alreadyRendered =
              renderedContinuousPagesRef.current.has(pageIndex) &&
              continuousRenderSignaturesRef.current.get(pageIndex) === renderSignature &&
              canvas.width === Math.round(viewport.width) &&
              canvas.height === Math.round(viewport.height);

            if (alreadyRendered) {
              continue;
            }

            canvas.width = viewport.width;
            canvas.height = viewport.height;
            continuousScaleRef.current.set(pageIndex, scale);
            setDefaultContinuousPageSize((previous) => previous ?? { width: viewport.width, height: viewport.height });
            setContinuousPageSizes((previous) => {
              const existingSize = previous[pageIndex];
              if (existingSize?.width === viewport.width && existingSize.height === viewport.height) {
                return previous;
              }
              return {
                ...previous,
                [pageIndex]: { width: viewport.width, height: viewport.height },
              };
            });
            await page.render({ canvasContext: context, viewport }).promise;
            if (generation !== continuousRenderGenerationRef.current || pdfRef.current !== pdf) return;
            const selectableItems = await buildSelectableTextItemsForPage({
              pdf,
              pdfjsLib,
              page: pageIndex,
            });
            if (generation !== continuousRenderGenerationRef.current || pdfRef.current !== pdf) return;
            setSelectableTextItems((previous) => ({
              ...previous,
              [pageIndex]: selectableItems,
            }));
            const pageHighlights = effectiveTextHighlights.filter((highlight) => !highlight.page || highlight.page === pageIndex);
            let generatedPageTextOverlays: PdfViewerOverlay[] = [];
            if (pageHighlights.length > 0) {
              generatedPageTextOverlays = await buildTextOverlaysForPage({
                pdf,
                pdfjsLib,
                page: pageIndex,
                viewport,
                scale,
                highlights: pageHighlights,
              });
            }
            if (generation !== continuousRenderGenerationRef.current || pdfRef.current !== pdf) return;
            setTextHighlightOverlays((previous) => [
              ...previous.filter((overlay) => overlay.page !== pageIndex),
              ...generatedPageTextOverlays,
            ]);
            continuousRenderSignaturesRef.current.set(pageIndex, renderSignature);
            setRenderedContinuousPages((previous) => {
              if (previous.has(pageIndex)) return previous;
              const next = new Set(previous);
              next.add(pageIndex);
              renderedContinuousPagesRef.current = next;
              return next;
            });
          } finally {
            renderingContinuousPagesRef.current.delete(pageIndex);
          }
        }

        setError(null);
      } catch {
        setError('Unable to render PDF pages.');
      }
    };

    if (!loading) {
      void renderContinuousPages();
    }
  }, [
    effectiveTargetPage,
    loading,
    overlayPagesKey,
    pageCount,
    pageNumber,
    renderKey,
    requestContinuousRender,
    effectiveTextHighlights,
    textHighlightsKey,
    viewMode,
    visibleContinuousPages,
  ]);

  useEffect(() => {
    if (!loading && pageCount > 0) {
      onPageChange?.(pageNumber, pageCount);
    }
  }, [pageNumber, pageCount, loading, onPageChange]);

  useEffect(() => {
    if (!activeTextSearchKey) {
      setTextSearchResult(null);
      autoScrolledTargetRef.current = null;
      return;
    }
    if (textSearchResult?.key === activeTextSearchKey) return;
    setTextSearchResult(null);
    autoScrolledTargetRef.current = null;
  }, [activeTextSearchKey, textSearchResult?.key]);

  useEffect(() => {
    let cancelled = false;

    const resolveTextTargetPage = async () => {
      const pdf = pdfRef.current;
      if (!pdf || loading || pageCount <= 0 || !activeTextSearch || !activeTextSearchKey) return;
      const searchOrder = prioritizePages(pageCount, targetPage);
      try {
        for (const page of searchOrder) {
          const pageText = await textContentForPage(pdf, page);
          if (cancelled) return;
          if (!pageMatchesHighlight(pageText, activeTextSearch)) continue;
          setTextSearchResult({ key: activeTextSearchKey, page });
          setPageNumber((previous) => (previous === page ? previous : page));
          return;
        }
        setTextSearchResult({ key: activeTextSearchKey, page: null });
      } catch {
        if (!cancelled) setTextSearchResult({ key: activeTextSearchKey, page: null });
      }
    };

    void resolveTextTargetPage();
    return () => {
      cancelled = true;
    };
  }, [activeTextSearch, activeTextSearchKey, loading, pageCount, targetPage, viewMode]);

  useEffect(() => {
    if (loading || pageCount <= 0 || effectiveTargetPage == null || textSearchPending) return;
    const safePage = Math.max(1, Math.min(pageCount, Math.trunc(effectiveTargetPage)));
    const scrollKey = `target:${activeTextSearchKey ?? 'numeric'}:${safePage}`;
    if (viewMode === 'continuous') {
      if (autoScrolledTargetRef.current !== scrollKey) {
        autoScrolledTargetRef.current = scrollKey;
        const pageElement = continuousPageRefs.current.get(safePage);
        pageElement?.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }
    setPageNumber((previous) => (previous === safePage ? previous : safePage));
  }, [activeTextSearchKey, effectiveTargetPage, loading, pageCount, textSearchPending, viewMode]);

  useEffect(() => {
    if (loading || !activeOverlayId || textHighlightOverlays.length === 0) return;
    const activeTextOverlay = textHighlightOverlays.find((overlay) => overlay.id === activeOverlayId);
    if (!activeTextOverlay) return;
    const scrollKey = `overlay:${activeOverlayId}:${activeTextOverlay.page}`;
    if (viewMode === 'continuous') {
      if (autoScrolledTargetRef.current !== scrollKey) {
        autoScrolledTargetRef.current = scrollKey;
        const pageElement = continuousPageRefs.current.get(activeTextOverlay.page);
        pageElement?.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    }
    setPageNumber((previous) => (previous === activeTextOverlay.page ? previous : activeTextOverlay.page));
  }, [activeOverlayId, loading, textHighlightOverlays, viewMode]);

  const scrollToPage = (page: number) => {
    const safePage = Math.max(1, Math.min(pageCount || 1, page));
    if (viewMode === 'continuous') {
      const pageElement = continuousPageRefs.current.get(safePage);
      pageElement?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
    setPageNumber(safePage);
  };

  const handleJumpSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = Number(jumpPageInput);
    if (!Number.isFinite(parsed)) {
      setJumpPageInput(String(pageNumber));
      return;
    }
    scrollToPage(parsed);
  };

  const executeFind = useCallback(async () => {
    const query = findQuery.trim();
    const pdf = pdfRef.current;
    const requestId = findRequestRef.current + 1;
    findRequestRef.current = requestId;
    if (!query || !pdf || loading || pageCount <= 0) {
      setFindResults([]);
      setActiveFindIndex(null);
      setLastFindQuery(query);
      return;
    }
    setFindSearching(true);
    setLastFindQuery(query);
    try {
      const normalizedQuery = normalizeSearchText(query);
      const results: PdfSearchResult[] = [];
      for (let page = 1; page <= pageCount; page += 1) {
        if (findRequestRef.current !== requestId || pdfRef.current !== pdf) return;
        let pageText = pageTextCacheRef.current.get(page);
        if (pageText == null) {
          pageText = await textContentForPage(pdf, page);
          if (findRequestRef.current !== requestId || pdfRef.current !== pdf) return;
          pageTextCacheRef.current.set(page, pageText);
        }
        const normalizedPage = normalizeSearchText(pageText);
        if (normalizedQuery && normalizedPage.includes(normalizedQuery)) {
          const lowerPageText = pageText.toLowerCase();
          const rawIndex = lowerPageText.indexOf(query.toLowerCase());
          const contextStart = rawIndex >= 0 ? Math.max(0, rawIndex - 90) : 0;
          const contextEnd = rawIndex >= 0
            ? Math.min(pageText.length, rawIndex + query.length + 90)
            : Math.min(pageText.length, 180);
          results.push({
            id: `find-${page}-${results.length}`,
            page,
            text: query,
            contextText: pageText.slice(contextStart, contextEnd),
          });
          if (results.length >= 200) break;
        }
        if (page % 8 === 0) {
          await nextAnimationFrame();
        }
      }
      if (findRequestRef.current !== requestId || pdfRef.current !== pdf) return;
      setFindResults(results);
      setActiveFindIndex(results.length > 0 ? 0 : null);
      if (results[0]) {
        scrollToPage(results[0].page);
      }
    } finally {
      if (findRequestRef.current === requestId) {
        setFindSearching(false);
      }
    }
  }, [findQuery, loading, pageCount]);

  useEffect(() => {
    const pdf = pdfRef.current;
    const result = activeFindResult;
    setTextHighlightOverlays((previous) => previous.filter((overlay) => !overlay.id.startsWith('find-')));
    if (!pdf || !result || loading) return;
    const scale = viewMode === 'continuous'
      ? continuousScaleRef.current.get(result.page)
      : renderScaleRef.current;
    const pageSize = viewMode === 'continuous'
      ? continuousPageSizes[result.page]
      : canvasSize;
    if (!scale || !pageSize?.width || !pageSize?.height) return;

    let cancelled = false;
    const buildFindOverlay = async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        const pdfPage = await pdf.getPage(result.page);
        if (cancelled || pdfRef.current !== pdf) return;
        const viewport = pdfPage.getViewport({ scale });
        const generated = await buildTextOverlaysForPage({
          pdf,
          pdfjsLib,
          page: result.page,
          viewport,
          scale,
          highlights: [{
            id: result.id,
            page: result.page,
            text: result.text,
            contextText: result.contextText,
            label: `Find: ${result.text}`,
          }],
        });
        if (cancelled || pdfRef.current !== pdf) return;
        setTextHighlightOverlays((previous) => [
          ...previous.filter((overlay) => !overlay.id.startsWith('find-')),
          ...generated,
        ]);
      } catch {
        // A failed find overlay should not affect PDF rendering.
      }
    };

    void buildFindOverlay();
    return () => {
      cancelled = true;
    };
  }, [
    activeFindResult?.id,
    activeFindResult?.page,
    activeFindResult?.text,
    activeFindResult?.contextText,
    canvasSize,
    continuousPageSizes,
    loading,
    viewMode,
  ]);

  const goToFindResult = useCallback(
    (nextIndex: number) => {
      if (findResults.length === 0) return;
      const bounded = ((nextIndex % findResults.length) + findResults.length) % findResults.length;
      setActiveFindIndex(bounded);
      scrollToPage(findResults[bounded].page);
    },
    [findResults],
  );

  const handleContinuousScroll = () => {
    if (viewMode !== 'continuous' || loading || pageCount <= 0) return;
    const surface = surfaceRef.current;
    if (!surface) return;
    const surfaceTop = surface.getBoundingClientRect().top;
    let bestPage = pageNumber;
    let bestDistance = Number.POSITIVE_INFINITY;

    continuousPageRefs.current.forEach((element, page) => {
      const distance = Math.abs(element.getBoundingClientRect().top - surfaceTop - 12);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestPage = page;
      }
    });

    if (bestPage !== pageNumber) {
      setPageNumber(bestPage);
    }
  };

  const activeScale = renderScaleRef.current;
  const combinedOverlays = useMemo(() => {
    const textOverlayIds = new Set(textHighlightOverlays.map((overlay) => overlay.id));
    return [
      ...(overlays || []).filter((overlay) => !textOverlayIds.has(overlay.id)),
      ...textHighlightOverlays,
    ];
  }, [overlays, textHighlightOverlays]);

  const visibleOverlays = useMemo(
    () => combinedOverlays.filter((overlay) => overlay.page === pageNumber),
    [combinedOverlays, pageNumber],
  );
  const pageRailPages = useMemo(() => {
    if (pageCount <= 1) return [];
    const pages = new Set<number>([1, pageCount, pageNumber]);
    for (let offset = -2; offset <= 2; offset += 1) {
      const page = pageNumber + offset;
      if (page >= 1 && page <= pageCount) pages.add(page);
    }
    for (let page = 10; page < pageCount; page += 10) {
      pages.add(page);
    }
    return [...pages].sort((a, b) => a - b);
  }, [pageCount, pageNumber]);

  const handleCanvasClick = (event: MouseEvent<HTMLCanvasElement>, page = pageNumber) => {
    if (!onCanvasClick) return;
    const canvas = viewMode === 'continuous'
      ? continuousCanvasRefs.current.get(page)
      : canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    const scale = viewMode === 'continuous'
      ? continuousScaleRef.current.get(page) || 1
      : renderScaleRef.current || 1;
    onCanvasClick({
      page,
      x: clickX / scale,
      y: clickY / scale,
    });
  };

  useEffect(() => {
    const handleMouseMove = (event: globalThis.MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const scale = drag.scale || renderScaleRef.current || 1;
      const nextX = Math.max(0, drag.startX + (event.clientX - drag.startClientX) / scale);
      const nextY = Math.max(0, drag.startY + (event.clientY - drag.startClientY) / scale);
      drag.currentX = nextX;
      drag.currentY = nextY;
      drag.moved = true;
      onOverlayMove?.(drag.overlayId, {
        page: drag.page,
        x: nextX,
        y: nextY,
      });
    };

    const handleMouseUp = () => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      if (!drag.moved) return;
      onOverlayMoveEnd?.(drag.overlayId, {
        page: drag.page,
        x: drag.currentX,
        y: drag.currentY,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onOverlayMove, onOverlayMoveEnd]);

  const overlaysInteractive = overlayPointerMode
    ? overlayPointerMode === 'auto'
    : Boolean(onOverlaySelect || onOverlayMove || onOverlayMoveEnd);

  const renderOverlay = (overlay: PdfViewerOverlay, scale: number) => (
    <button
      key={overlay.id}
      type="button"
      onMouseDown={(event) => {
        if (!overlaysInteractive) return;
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        onOverlaySelect?.(overlay.id);
        dragRef.current = {
          overlayId: overlay.id,
          page: overlay.page,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startX: overlay.x,
          startY: overlay.y,
          scale,
          currentX: overlay.x,
          currentY: overlay.y,
          moved: false,
        };
      }}
      onClick={(event) => {
        if (!overlaysInteractive) return;
        event.stopPropagation();
        onOverlaySelect?.(overlay.id);
      }}
      className={`absolute z-20 rounded border ${
        overlay.id === effectiveActiveOverlayId
          ? 'border-blue-600 bg-blue-100/30 cursor-move'
          : 'border-blue-400 bg-blue-50/20 cursor-move'
      }`}
      style={{
        left: `${overlay.x * scale}px`,
        top: `${overlay.y * scale}px`,
        width: `${Math.max(6, overlay.width) * scale}px`,
        height: `${Math.max(6, overlay.height) * scale}px`,
        pointerEvents: overlaysInteractive ? undefined : 'none',
      }}
      title={overlay.label || 'Overlay field'}
    >
      {overlay.label && (
        <span className="absolute -top-4 left-0 whitespace-nowrap rounded bg-white/90 px-1 text-[10px] text-blue-700">
          {overlay.label}
        </span>
      )}
    </button>
  );

  const selectionAttributes = (page: number) => ({
    'data-lex-selectable': 'true',
    'data-lex-selection-source': selectionMetadata?.sourceSurface ?? 'pdf_preview',
    'data-lex-selection-label': selectionMetadata?.label ?? 'PDF preview',
    'data-lex-document-id': selectionMetadata?.documentId ?? undefined,
    'data-lex-document-version-id': selectionMetadata?.versionId ?? undefined,
    'data-lex-document-title': selectionMetadata?.documentTitle ?? undefined,
    'data-lex-matter-id': selectionMetadata?.matterId ?? undefined,
    'data-lex-client-id': selectionMetadata?.clientId ?? undefined,
    'data-lex-page': String(page),
  });

  const renderTextLayer = (page: number, scale: number) => {
    const items = selectableTextItems[page] || [];
    if (items.length === 0) return null;
    return (
      <div
        className="absolute inset-0 z-10 cursor-text select-text overflow-hidden text-transparent"
        style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
        {...selectionAttributes(page)}
      >
        {items.map((item) => (
          <span
            key={item.id}
            className="absolute whitespace-pre text-transparent selection:bg-blue-300/45"
            style={{
              left: `${item.x * scale}px`,
              top: `${item.y * scale}px`,
              width: `${item.width * scale}px`,
              height: `${item.height * scale}px`,
              fontSize: `${item.fontSize * scale}px`,
              lineHeight: `${item.height * scale}px`,
              userSelect: 'text',
              WebkitUserSelect: 'text',
            }}
          >
            {item.text}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full min-w-0 ${className ?? ''}`} ref={containerRef}>
      <div className="mb-2 flex flex-col gap-2 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center justify-between gap-2">
          <span>{loading ? 'Loading PDF\u2026' : `Page ${pageNumber} of ${pageCount || 1}`}</span>
          <div className="flex items-center gap-1">
            <form onSubmit={handleJumpSubmit} className="flex items-center gap-1">
              <span className="text-[11px] text-slate-500">Jump</span>
              <input
                type="number"
                min={1}
                max={pageCount || 1}
                value={jumpPageInput}
                onChange={(event) => setJumpPageInput(event.target.value)}
                className="h-7 w-16 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                aria-label="Jump to page"
              />
            </form>
          <button
            type="button"
            onClick={() => scrollToPage(pageNumber - 1)}
            disabled={pageNumber <= 1 || loading}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollToPage(pageNumber + 1)}
            disabled={pageNumber >= (pageCount || 1) || loading}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
          </div>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void executeFind();
          }}
          className="flex items-center gap-1"
        >
          <input
            type="search"
            value={findQuery}
            onChange={(event) => setFindQuery(event.target.value)}
            placeholder="Find in document..."
            className="h-7 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            aria-label="Find in document"
          />
          <button
            type="submit"
            disabled={loading || findSearching || !findQuery.trim()}
            className="h-7 rounded-md border border-slate-200 px-2 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {findSearching ? 'Finding' : 'Find'}
          </button>
          <button
            type="button"
            onClick={() => goToFindResult((activeFindIndex ?? 0) - 1)}
            disabled={findResults.length === 0}
            className="h-7 rounded-md border border-slate-200 px-2 text-[11px] text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => goToFindResult((activeFindIndex ?? -1) + 1)}
            disabled={findResults.length === 0}
            className="h-7 rounded-md border border-slate-200 px-2 text-[11px] text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
          <span className="w-16 text-right text-[11px] text-slate-500">
            {findSearching
              ? '...'
              : findResults.length > 0 && activeFindIndex != null
                ? `${activeFindIndex + 1}/${findResults.length}`
                : lastFindQuery && lastFindQuery === findQuery.trim()
                  ? 'No hits'
                  : ''}
          </span>
        </form>
        {viewMode === 'continuous' && pageRailPages.length > 0 && (
          <div className="flex items-center gap-1 overflow-x-auto pb-0.5">
            <span className="shrink-0 text-[11px] text-slate-500">Pages</span>
            {pageRailPages.map((page, index) => {
              const previous = pageRailPages[index - 1];
              const gap = previous != null && page - previous > 1;
              return (
                <span key={page} className="flex shrink-0 items-center gap-1">
                  {gap && <span className="text-[11px] text-slate-400">...</span>}
                  <button
                    type="button"
                    onClick={() => scrollToPage(page)}
                    className={`rounded border px-2 py-0.5 text-[11px] ${
                      page === pageNumber
                        ? 'border-blue-300 bg-blue-50 font-semibold text-blue-800'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {page}
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
      <div
        ref={surfaceRef}
        onScroll={handleContinuousScroll}
        className={`flex-1 w-full overflow-auto rounded-xl border p-3 ${surfaceClassName ?? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
      >
        {loading ? (
          <div className="h-full w-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
            Rendering preview&hellip;
          </div>
        ) : error ? (
          <div className="h-full w-full flex items-center justify-center text-xs text-red-600 dark:text-red-400">
            {error}
          </div>
        ) : viewMode === 'continuous' ? (
          <div className="mx-auto flex w-full flex-col items-center gap-4 pb-8">
            {Array.from({ length: pageCount }, (_, index) => index + 1).map((page) => {
              const pageSize = continuousPageSizes[page] ?? defaultContinuousPageSize ?? { width: 320, height: 420 };
              const scale = continuousScaleRef.current.get(page) ?? 1;
              const pageOverlays = combinedOverlays.filter((overlay) => overlay.page === page);
              return (
                <div
                  key={page}
                  ref={getContinuousPageRef(page)}
                  data-pdf-page={page}
                  className="w-full"
                  aria-label={`Page ${page}`}
                >
                  <div className="mb-1 flex items-center justify-between px-1 text-[11px] text-gray-500 dark:text-gray-400">
                    <span>Page {page}</span>
                    {pageOverlays.length > 0 && <span>{pageOverlays.length} evidence anchor{pageOverlays.length === 1 ? '' : 's'}</span>}
                  </div>
                  <div
                    className="relative mx-auto bg-white shadow-sm"
                    style={{
                      width: `${pageSize.width}px`,
                      height: `${pageSize.height}px`,
                    }}
                  >
                    <canvas
                      ref={getContinuousCanvasRef(page)}
                      className="block"
                      onClick={(event) => handleCanvasClick(event, page)}
                    />
                    {!renderedContinuousPages.has(page) && (
                      <div className="absolute inset-0 z-0 flex items-center justify-center bg-white text-[11px] text-slate-400">
                        Rendering page {page}...
                      </div>
                    )}
                    {renderTextLayer(page, scale)}
                    {pageOverlays.map((overlay) => renderOverlay(overlay, scale))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="relative mx-auto"
            style={{
              width: `${canvasSize.width}px`,
              height: `${canvasSize.height}px`,
            }}
          >
            <canvas ref={setSingleCanvasRef} className="block" onClick={(event) => handleCanvasClick(event)} />
            {renderTextLayer(pageNumber, activeScale)}
            {visibleOverlays.map((overlay) => renderOverlay(overlay, activeScale))}
          </div>
        )}
      </div>
    </div>
  );
}
